import React, { useCallback, useReducer } from 'react';

import ApiService from '@zola-helpers/client/dist/es/http/api';

import type { CamelCasedPropertiesDeep } from 'type-fest/index';
import type {
  WCartUpdateItemViewResponse,
  WGetCartView,
  WGetCartViewResponse,
  WProjectFullView,
  WProjectListingCustomizationView,
} from '@zola/svc-web-api-ts-client';
import type { ConvertWebApiDeclaredToActual } from '@zola-helpers/client/dist/es/@types/svc-web-api';
import { createAction, createReducer } from '@reduxjs/toolkit';
import { useEffectOnce } from '../../hooks/useEffectOnce';
import { defaultCartContext, CartContext, CartContextValue } from './CartContext';
import { isCardWithSimpleSignup } from '../../paper/cards/constants/cardTypeFeatures/cardTypesWithSimpleSignup';
import { findLeadCustomization } from '../../paper/cards/util/customization';
import { PaperOnboardTypes } from '../PaperOnboard/constants';
import { usePaperOnboardContext } from '../PaperOnboard/PaperOnboardContext';

type WGetCartViewType = ConvertWebApiDeclaredToActual<CamelCasedPropertiesDeep<WGetCartView>>;

const receivedCartAction =
  createAction<Omit<CartContextValue, 'isHomeStore' | 'cardProjects' | 'loaded'>>('RECEIVED_CART');

const determinedHomeStoreAction = createAction<boolean>('DETERMINED_HOME_STORE');

const receivedCardProjectsAction =
  createAction<ConvertWebApiDeclaredToActual<WProjectFullView>[]>('RECEIVED_CARD_PROJECTS');

const loadedCartAction = createAction('LOADED_CART');

const cardState = createReducer<CartContextValue>(defaultCartContext, (builder) => {
  builder
    .addCase(receivedCartAction, (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    })
    .addCase(determinedHomeStoreAction, (state, action) => {
      return {
        ...state,
        isHomeStore: action.payload,
      };
    })
    .addCase(receivedCardProjectsAction, (state, action) => {
      return {
        ...state,
        loaded: true,
        cardProjects: {
          ...state.cardProjects,
          ...action.payload.reduce(
            (acc, project) => ({
              ...acc,
              [project.uuid as string]: project,
            }),
            {} as CartContextValue['cardProjects']
          ),
        },
      };
    })
    .addCase(loadedCartAction, (state) => {
      return {
        ...state,
        loaded: true,
      };
    });
});

const parseCartItemData = (cartItemData: WGetCartViewType, busy = false) => {
  return {
    busy,
    initialized: true,
    size: cartItemData.items?.length || 0,
    items: cartItemData.items || [],
    totals: cartItemData.totals || null,
    freeShipping: cartItemData.freeShipping || null,
    cartId: cartItemData.cartId,
    registryId: cartItemData.registryId,
    isBaby:
      cartItemData.store?.toString() === 'BABY_REGISTRY' ||
      cartItemData.store?.toString() === 'BABY_STORE',
    /** @ts-expect-error: This property doesn't seem to exist, but kept here during the TS/redux-tool kit conversion just in case */
    isFreeShippingEligible: cartItemData.is_free_shipping_eligible,
  };
};

export const CartProvider: React.FC<{
  errorToastFn: (msg: string) => void;
}> = ({ children, errorToastFn }) => {
  const [state, dispatch] = useReducer(cardState, defaultCartContext);

  const { isPushSignupIntoCustomization, openPaperOnboard, userContext } = usePaperOnboardContext();

  const fetchCart = useCallback(() => {
    return ApiService.get<
      ConvertWebApiDeclaredToActual<CamelCasedPropertiesDeep<WGetCartViewResponse>>
    >(`/website-nav/web-api/v1/cart`)
      .then((json) => {
        const { data } = json;
        if (data) {
          dispatch(receivedCartAction(parseCartItemData(data)));
          return (data.items || [])
            .map((item) => item.projectUuid)
            .filter((uuid): uuid is string => Boolean(uuid));
        }
        return [];
      })
      .then((cardProjectUuids: string[]) => {
        return Promise.all(
          cardProjectUuids.map((uuid) => {
            // Catch here, we can still check out, we just won't have the card preview
            return ApiService.get<ConvertWebApiDeclaredToActual<WProjectFullView>>(
              `/web-api/v2/card-project/${uuid}`
            ).catch(() => null);
          })
        );
      })
      .then((cardProjects) => {
        const filteredCardProjects = cardProjects.filter(
          (project): project is ConvertWebApiDeclaredToActual<WProjectFullView> => Boolean(project)
        );
        dispatch(receivedCardProjectsAction(filteredCardProjects));
      })
      .catch(() => dispatch(loadedCartAction()));
  }, []);

  const onRemoveCartItem = useCallback((cartData: WGetCartViewType) => {
    const cartView = parseCartItemData(cartData);
    dispatch(receivedCartAction(cartView));
    // dispatching this event so that the cart count in nav can update via getting the new cart size  (legacy)
    window.dispatchEvent(new CustomEvent('CART_MODAL_REMOVE_ITEM', { detail: 'NAV' }));
    // dispatch event to update the cart size in navs (regular and website)
    window.dispatchEvent(
      new CustomEvent('NAV_UPDATE_CART_SIZE', { detail: cartView.items.length })
    );
  }, []);

  const removeCartItem = useCallback(
    (cartItemId: string) => {
      return ApiService.delete<
        ConvertWebApiDeclaredToActual<CamelCasedPropertiesDeep<WGetCartViewResponse>>
      >(`/website-nav/web-api/v1/cart/item/id/${cartItemId}`).then((json) => {
        const { data } = json;
        if (data) onRemoveCartItem(data);
      });
    },
    [onRemoveCartItem]
  );

  const removeCartItems = useCallback(
    (itemIds: string[], skipPaperOnboarding?: boolean) => {
      const remove = () =>
        ApiService.post<
          ConvertWebApiDeclaredToActual<CamelCasedPropertiesDeep<WGetCartViewResponse>>
        >(`/website-nav/web-api/v1/cart/items`, { itemIds }).then((json) => {
          const { data } = json;
          if (data) onRemoveCartItem(data);
        });

      const { cardProjects, items } = state;
      const maybeOpenPaperOnboard =
        isPushSignupIntoCustomization && userContext.is_guest && items.length > 0;

      /**
       * Open paper onboard for guests if they have editable card projects in their cart, not including paper add-ons
       */
      if (maybeOpenPaperOnboard && !skipPaperOnboarding) {
        const projectUuid = items.find((item) => item.id === itemIds[0])?.projectUuid;
        const { customizations, editable } = cardProjects[projectUuid || ''] || {};
        if (customizations && editable) {
          const projectType = findLeadCustomization(
            customizations as WProjectListingCustomizationView[]
          )?.type;
          if (projectType) {
            openPaperOnboard({
              paperOnboardCallback: () => {
                remove().catch(() => null);
              },
              paperOnboardType: PaperOnboardTypes.SAVE_PROGRESS_REMOVE,
              projectType,
              redirectHref: '/wedding-planning/projects/drafts',
            });
            return Promise.resolve();
          }
        }
      }

      /**
       * Otherwise immediately remove item from cart
       */
      return remove();
    },
    [isPushSignupIntoCustomization, onRemoveCartItem, openPaperOnboard, state, userContext.is_guest]
  );

  const updateCartItemQuantity = useCallback(
    (id: string, quantity: number) => {
      return ApiService.put<
        ConvertWebApiDeclaredToActual<CamelCasedPropertiesDeep<WCartUpdateItemViewResponse>>
      >(`/website-nav/web-api/v1/cart/item/id/${id}`, { quantity }).then((json) => {
        const { data } = json;
        if (data?.errorMessage) {
          const error = new Error(data.errorMessage);
          /** @ts-expect-error This should be ErrorWithResponse probably.  Are we using the response? probably not */
          error.response = json.data;
          errorToastFn(data.errorMessage);
        }

        if (json.data?.cart) {
          dispatch(receivedCartAction(parseCartItemData(json.data?.cart)));
        }
      });
    },
    [errorToastFn]
  );

  useEffectOnce(() => {
    const isHomeStore =
      window.location.hostname.toLowerCase().indexOf('homestore.zola.com') > -1 || false;

    dispatch(determinedHomeStoreAction(isHomeStore));
  });

  const onCheckout = useCallback(() => {
    const queryString = state.isHomeStore ? '?store=true' : '';
    const redirectHref = `/registry/cart/checkout${queryString}`;
    const { cardProjects, items } = state;
    const maybeOpenPaperOnboard =
      isPushSignupIntoCustomization && userContext.is_guest && items.length > 0;

    /**
     * Open paper onboard for guests if they have card projects in their cart, including paper add-ons
     */
    if (maybeOpenPaperOnboard) {
      const projectTypesInCart: string[] = [];
      items.forEach((item) => {
        const { projectUuid } = item;
        const { customizations } = cardProjects[projectUuid || ''] || {};
        if (customizations) {
          const { type } =
            findLeadCustomization(customizations as WProjectListingCustomizationView[]) || {};
          if (type) {
            projectTypesInCart.push(type);
          }
        }
      });
      /**
       * If there are multiple projects in cart that require both simple and non-simple signup
       * prioritize opening non-simple signup to gather partner detail and wedding date
       */
      const projectType =
        projectTypesInCart.filter((type) => !isCardWithSimpleSignup(type))[0] ||
        projectTypesInCart.filter((type) => isCardWithSimpleSignup(type))[0];
      if (projectType) {
        openPaperOnboard({
          paperOnboardType: PaperOnboardTypes.QUICK_SIGNUP_CHECKOUT,
          projectType,
          redirectHref,
        });
        return;
      }
    }

    /**
     * Otherwise redirect to checkout
     */
    window.location.href = redirectHref;
  }, [isPushSignupIntoCustomization, openPaperOnboard, state, userContext]);

  const getCardProject = useCallback(
    (cardProjectUuid: string) => {
      const project = state.cardProjects[cardProjectUuid];
      if (project) return project;
      return null;
    },
    [state.cardProjects]
  );

  const getCardCustomization = useCallback(
    (cardProjectUuid: string, customizationUuid: string) => {
      const project = getCardProject(cardProjectUuid);
      if (project) {
        const customization = (project.customizations || []).find(
          (customization) => customization.uuid === customizationUuid
        );
        if (customization) {
          return customization;
        }
      }
      return null;
    },
    [getCardProject]
  );

  useEffectOnce(() => {
    fetchCart().catch(() => null);
  });

  return (
    <CartContext.Provider
      value={{
        ...state,
        fetchCart,
        removeCartItem,
        removeCartItems,
        updateCartItemQuantity,
        onCheckout,
        getCardCustomization,
        getCardProject,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
